/* global google */
import React, { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';

const PlaceAutocomplete = forwardRef(({ onPlaceSelect }, ref) => {
  const autocompleteRef = useRef(null);
  // This lets the parent component (LandingPage) get a ref to the DOM element
  useImperativeHandle(ref, () => autocompleteRef.current);

  useEffect(() => {
    const autocompleteEl = autocompleteRef.current;
    if (!autocompleteEl) return;

    // Try to focus the input field inside the web component
    setTimeout(() => {
      try {
        const input = autocompleteEl.querySelector('input') || autocompleteEl.shadowRoot?.querySelector('input');
        if (input) {
          input.focus();
        } else {
          // Fallback: try to focus the web component itself
          autocompleteEl.focus();
        }
      } catch (e) {
        // If shadow DOM access fails, try focusing the component directly
        autocompleteEl.focus();
      }
    }, 100);

    const handleSelect = async (event) => {
      // New Places Autocomplete event returns a PlacePrediction
      const { placePrediction } = event || {};
      if (!placePrediction) return;

      let addressString = '';

      try {
        if (typeof placePrediction.toPlace === 'function') {
          // Convert the prediction to a full Place object to access formattedAddress
          const place = placePrediction.toPlace();
          if (place && typeof place.fetchFields === 'function') {
            await place.fetchFields({ fields: ['displayName', 'formattedAddress'] });
          }
          addressString = place.formattedAddress || place.displayName || '';
        }
      } catch (_) {
        // Swallow errors – we'll fall back to simple text properties below.
      }

      // Fallbacks if the richer API isn't available yet
      if (!addressString) {
        addressString = placePrediction.text || placePrediction.description || '';
      }

      if (addressString) {
        // Normalise shape for the parent: provide formatted_address or name
        onPlaceSelect({ formatted_address: addressString, name: addressString });
      }
    };

    // Listen for the correct event in the beta channel
    autocompleteEl.addEventListener('gmp-select', handleSelect);

    return () => {
      autocompleteEl.removeEventListener('gmp-select', handleSelect);
    };
  }, [onPlaceSelect]);
  
  // In React, you use lowercase for web components.
  // The input is part of the web component's shadow DOM.
  // We can't style it directly from here with className, but we can use CSS custom properties if the component exposes them, or rely on its default styling.
  return <gmp-place-autocomplete ref={autocompleteRef} placeholder="Enter address or lot name..." autoFocus></gmp-place-autocomplete>;
});

export default PlaceAutocomplete; 